/* eslint-disable camelcase */
import React from 'react'
import { createProductGroup } from 'app-support/productgroup/state/actions'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { IRootReducerState } from 'state/reducers'
import { Box, Button, Card, CardContent, CardHeader, Divider, InputAdornment, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { IProductGroup } from '@cbgms/api'
import { useQueryClient } from '@cbgms/base/http/use-query'
import { productTreeCacheKey } from '../hooks/cache-keys'
import { useNotifier } from '@cbgms/base/notifier'
import { useHistory } from 'react-router-dom'
import { Dropdown, DropdownOption, TextField } from '@carsys/ui'

interface MainGroupProps {
  ProductGroup: IProductGroup
}

export function MainGroup(props: MainGroupProps) {
  const { ProductGroup } = props
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const queryClient = useQueryClient()
  const notifier = useNotifier()
  const mainGroups = useSelector((state: IRootReducerState) => state.productGroup.tree?.Categories || [])
  const [mainGroupUUID, setMainGroupUUID] = React.useState<string>('')
  const [newMainGroupName, setNewMainGroupName] = React.useState<string>('')

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setMainGroupUUID(event.target.value as string)
  }

  const handleSubmit = async () => {
    const newMainGroup: Partial<IProductGroup> = {
      Name: newMainGroupName,
      SortOrder: 0,
      ParentUUID: ProductGroup.UUID,
      Locale: ProductGroup.Locale
    }

    try {
      await dispatch(createProductGroup(newMainGroup))
      notifier.success(t('global.successfully_added'))
      queryClient.invalidateQueries(productTreeCacheKey(ProductGroup.UUID))
    } catch (e: any) {
      notifier.error(t('global.error'), e)
      throw new Error(e)
    }
  }

  const goToGroup = () => {
    if (mainGroupUUID) {
      history.push(`/product-group/${ProductGroup.UUID}/main-group/${mainGroupUUID}`)
    }
  }

  return (
    <Card variant='outlined'>
      <CardHeader title={<Typography variant='h6'>{t('productgroup.tree_maingroup_title')}</Typography>} />
      <Divider />
      <CardContent sx={{ p: 2 }}>
        <Typography>{t('productgroup.tree_maingroup_info')}</Typography>
        <Typography sx={{ fontWeight: 700, mt: 2 }}>{t('productgroup.subTitle_Create')}</Typography>
        <TextField
          data-testid='maingroup-name-input'
          fullWidth
          required
          size='small'
          label={t('productgroup.name')}
          value={newMainGroupName}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setNewMainGroupName(event.target.value)
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <Button
                  data-testid='add-maingroup-btn'
                  disabled={!newMainGroupName}
                  variant='outlined'
                  size='small'
                  startIcon={<AddIcon />}
                  onClick={handleSubmit}
                >
                  {t('productgroup.tree_maingroup_add')}
                </Button>
              </InputAdornment>
            )
          }}
          sx={{ mt: 1 }}
        />
        <Divider sx={{ mt: 3, mb: 2 }}>{t('global.or')}</Divider>

        <Box sx={{ opacity: !mainGroups || mainGroups.length === 0 ? 0.4 : 1 }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>{t('productgroup.subTitle_Edit')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Dropdown
              label={t('productgroup.tree_maingroup_select')}
              value={mainGroupUUID}
              disabled={!mainGroups || mainGroups.length === 0}
              onChange={event => handleSelect(event)}
              sx={{ flex: '1 1 75%' }}
            >
              {mainGroups.map((group, index) => {
                return (
                  <DropdownOption key={`mainGroupItem-select-${index}`} value={group.UUID}>
                    {group.Name}
                  </DropdownOption>
                )
              })}
            </Dropdown>
            <Button variant='outlined' disabled={!mainGroupUUID} onClick={goToGroup} startIcon={<EditIcon />} sx={{ ml: 2 }}>
              {t('productgroup.tree_maingroup_edit')}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default MainGroup
